import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function MiniReviewBadge({ rating, total }) {
  if (total <= 0) return null;
  return (
    <div className="review-badge-pulse bg-green-50/60 rounded-lg p-3 flex items-center justify-center gap-2 text-sm text-gray-600">
      <GoogleLogo />
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
        ))}
      </div>
      <span className="font-semibold text-gray-900">{rating.toFixed(1)}</span>
      <span className="text-gray-400">({total} avis)</span>
    </div>
  );
}

export default function ContactSection({ rating = 5, totalReviews = 0 }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !phone.trim()) {
      setError("Veuillez remplir votre nom et téléphone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { name: name.trim(), phone: phone.trim(), service, message: message.trim() });
      setSuccess(true);
      setName("");
      setPhone("");
      setService("");
      setMessage("");
    } catch (err) {
      setError("Une erreur est survenue. Veuillez nous appeler directement au 06 28 51 49 15.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 md:py-24 bg-[#F9FAFB]">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-12 md:mb-16 scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#6AB023] mb-4" data-testid="contact-title">Contactez-nous</h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">Demandez votre devis gratuit — sans engagement. François vous rappelle sous 24h.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-5xl mx-auto">
          <Card className="scroll-reveal-left bg-white border border-gray-200 rounded-xl shadow-lg">
            <CardContent className="p-6 md:p-8">
              {success ? (
                <div className="flex flex-col items-center text-center py-8" data-testid="contact-success">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-[#6AB023]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
                  <p className="text-gray-600 text-sm">François vous rappelle dans les 24h pour planifier votre visite technique gratuite.</p>
                  <Button onClick={() => setSuccess(false)} variant="outline" className="mt-6 border-[#6AB023] text-[#6AB023] hover:bg-green-50 rounded-full" data-testid="contact-new-request-btn">Nouvelle demande</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">Devis gratuit — sans engagement</h3>
                    <p className="text-sm text-gray-500">François vous rappelle sous 24h.</p>
                  </div>
                  <MiniReviewBadge rating={rating} total={totalReviews} />
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1.5">Votre nom <span className="text-red-400">*</span></label>
                    <Input id="contact-name" type="text" placeholder="Jean Dupont" value={name} onChange={(e) => setName(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-1.5">Votre téléphone <span className="text-red-400">*</span></label>
                    <Input id="contact-phone" type="tel" placeholder="06 12 34 56 78" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label htmlFor="contact-service" className="block text-sm font-medium text-gray-700 mb-1.5">Type de projet</label>
                    <select id="contact-service" value={service} onChange={(e) => setService(e.target.value)} className="w-full h-12 border border-gray-300 rounded-lg px-3 text-sm text-gray-700 bg-white contact-input-focus focus:outline-none" data-testid="contact-service-select">
                      <option value="">Sélectionnez un service</option>
                      <option value="PAC Air/Eau">Installation PAC Air/Eau</option>
                      <option value="PAC Air/Air">Installation PAC Air/Air</option>
                      <option value="Chauffe-eau thermodynamique">Chauffe-eau thermodynamique</option>
                      <option value="Climatisation réversible">Climatisation réversible</option>
                      <option value="Remplacement chaudière fioul">Remplacement chaudière fioul</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1.5">Décrivez votre besoin <span className="text-xs text-gray-400 font-normal">(optionnel)</span></label>
                    <textarea id="contact-message" placeholder="Ex: Remplacement de ma chaudière fioul, maison 120m2..." value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white contact-input-focus focus:outline-none resize-none" data-testid="contact-message-input" />
                  </div>
                  {error && <p className="text-sm text-red-600" data-testid="contact-error">{error}</p>}
                  <Button type="submit" disabled={loading} className="btn-shine w-full bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full h-12 text-base gap-2" data-testid="contact-submit-btn">
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Envoi en cours...
                      </span>
                    ) : (
                      <><Send className="w-5 h-5" />Recevoir mon devis gratuit</>
                    )}
                  </Button>
                  <p className="text-xs text-gray-400 text-center">Sans engagement · Vos données restent confidentielles</p>
                </form>
              )}
            </CardContent>
          </Card>
          <div className="flex flex-col gap-5 scroll-reveal-right">
            <div className="space-y-3">
              <a href="tel:0628514915" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group" data-testid="contact-phone-link">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors">
                  <Phone className="w-5 h-5 text-[#6AB023] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">06 28 51 49 15</p>
                  <p className="text-xs text-gray-500">Appel gratuit · Du lundi au samedi</p>
                </div>
              </a>
              <a href="mailto:frs.delattre@gmail.com" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group" data-testid="contact-email-link">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors">
                  <Mail className="w-5 h-5 text-[#6AB023] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">frs.delattre@gmail.com</p>
                  <p className="text-xs text-gray-500">Réponse sous 24h</p>
                </div>
              </a>
              <a href="https://maps.app.goo.gl/C8s7vSJPZ2sHXxe16" target="_blank" rel="noreferrer" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors">
                  <MapPin className="w-5 h-5 text-[#6AB023] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">1899 Côte de Pinel Garrigou</p>
                  <p className="text-xs text-gray-500">47380 Pinel-Hauterive · Lot-et-Garonne</p>
                </div>
              </a>
            </div>
            {/* Réassurance */}
            <div className="bg-green-50/60 rounded-xl p-5 space-y-3 mt-2">
              <p className="text-sm font-semibold text-gray-900">Ce que vous obtenez :</p>
              <div className="space-y-2">
                {[
                  "Visite technique gratuite à domicile",
                  "Devis détaillé avec aides déduites",
                  "Accompagnement dossiers MaPrimeRénov'",
                  "Installation par un artisan certifié RGE",
                ].map((item) => (
                  <p key={item} className="flex items-start gap-2 text-sm text-gray-700"><CheckCircle className="w-4 h-4 text-[#6AB023] flex-shrink-0 mt-0.5" />{item}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
